import React, { useEffect, useState } from 'react';
import FeeSlider from './FeeSlider';
import UnitCountInput from './UnitCountInput';
import loader from '../../../../assets/loader.svg';
import { formatNumberWithCommas } from '../../../../utils';
import FeeSwitch from './FeeSwitch';

interface PlatformFeeProps {
  initialFeePerUnit: number;
  initialUnitCount: number;
  initialBillingCycle: 'Monthly' | 'Annually';
  initialSaasFeeEnabled: 'Yes' | 'No';
  onFeePerUnitChange: (value: number) => void;
  onUnitCountChange: (value: number) => void;
  onBillingCycleChange: (cycle: 'Monthly' | 'Annually') => void;
  onSaasFeeEnabledChange: (value: 'Yes' | 'No') => void;
  isLoadingTotal: boolean;
  total: number;
}

const PlatformFee: React.FC<PlatformFeeProps> = ({
  initialFeePerUnit,
  initialUnitCount,
  initialBillingCycle,
  initialSaasFeeEnabled,
  onFeePerUnitChange,
  onUnitCountChange,
  onBillingCycleChange,
  onSaasFeeEnabledChange,
  isLoadingTotal,
  total,
}) => {
  const [billingCycle, setBillingCycle] = useState<'Monthly' | 'Annually'>(initialBillingCycle);
  const [feePerUnit, setFeePerUnit] = useState(initialFeePerUnit);
  const [saasFeeEnabled, setSaasFeeEnabled] = useState(initialSaasFeeEnabled);

  useEffect(() => {
    setBillingCycle(initialBillingCycle);
    setFeePerUnit(initialFeePerUnit);
    setSaasFeeEnabled(initialSaasFeeEnabled);
  }, [initialBillingCycle, initialFeePerUnit, initialSaasFeeEnabled]);

  const handleBillingCycleChange = (cycle: 'Monthly' | 'Annually') => {
    setBillingCycle(cycle);
    onBillingCycleChange?.(cycle);
  };

  const handleFeePerUnitChange = (value: number) => {
    setFeePerUnit(value);
    onFeePerUnitChange?.(value);
  };

  return (
    <div
      style={{
        width: '30vh',
        height: '20vh',
        backgroundColor: '#ffffff',
        borderRadius: '1.6vh',
        boxShadow: '0vh 0.8vh 3.2vh -0.4vh rgba(16, 29, 59, 0.1)',
        padding: '1.6vh 0',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
    >
      {/* Platform Fee Billed Section */}
      <div>
        <FeeSwitch
          initialSaasFeeEnabled={saasFeeEnabled}
          onSaasFeeEnabledChange={onSaasFeeEnabledChange}
          billingCycle={billingCycle}
          handleBillingCycleChange={handleBillingCycleChange}
        />
      </div>

      <div
        style={{
          width: '100%',
          height: '0.1vh',
          backgroundColor: 'rgba(16, 29, 59, 0.1)',
        }}
      />

      {/* Fee per Unit Section */}
      <div style={{ padding: '0 1.6vh' }}>
        <FeeSlider setOrder={handleFeePerUnitChange} initialOrder={feePerUnit} enabled={saasFeeEnabled === 'Yes'} />
      </div>

      <div
        style={{
          width: '100%',
          height: '0.1vh',
          backgroundColor: 'rgba(16, 29, 59, 0.1)',
        }}
      />

      {/* Contracted Unit Count Section */}
      <UnitCountInput
        onUnitCountChange={onUnitCountChange}
        initialUnitCount={initialUnitCount}
        enabled={saasFeeEnabled === 'Yes'}
      />

      <div
        style={{
          width: '100%',
          height: '0.1vh',
          backgroundColor: 'rgba(16, 29, 59, 0.1)',
        }}
      />

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: '0 1.6vh',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ fontSize: '1.4vh', fontWeight: '700', color: '#101D3B', textAlign: 'left' }}>
          Period Total Cost
        </div>
        {isLoadingTotal ? (
          <img src={loader} className="rotating" alt="logout" style={{ width: '1.9vh', height: '1.9vh' }} />
        ) : (
          <div
            style={{ fontSize: '1.4vh', fontWeight: '700', color: saasFeeEnabled === 'Yes' ? '#00AB9B' : '#9FA5B1' }}
          >
            ${formatNumberWithCommas(total, 2)}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlatformFee;
